'use client';

import Link from 'next/link';
import Image from 'next/image';
import NewsSentiment from '@/components/(postTypes)/news/NewsSentiment';
import React from 'react';
import { ImageNode } from '@/types/global';
import dynamic from 'next/dynamic';
import { Skeleton } from '@/components/ui/skeleton';

const TimeSinceClient = dynamic(
    () => import('@/components/(postTypes)/news/Single/TimeSinceClient'),
    {
        ssr: false,
        loading: () => <Skeleton className='h-[15px] w-[60px] rounded-xl' />,
    },
);

interface NewsCardProps {
    title: string;
    index: number;
    uri: string;
    date: string;
    categories: {
        name: string;
        slug: string;
        newsCategoryImage?: {
            icon: {
                mediaItemUrl: string;
            };
        };
    }[];
    page?: string;
    databaseId: number;
    sentiment?: { bearish?: number; bullish?: number } | undefined;
    featuredImage?: ImageNode;
    id?: string;
}

const NewsCardSideBar: React.FC<NewsCardProps> = ({
    title,
    uri,
    date,
    sentiment,
    databaseId,
    featuredImage,
    id,
}) => {
    return (
        <div
            id={id}
            className={`overflow-hidden bg-gray-50 dark:bg-white/5 border dark:border-white/10 rounded-lg p-1 flex gap-2 w-full justify-between items-center transition-all`}
        >
            {featuredImage?.node?.sourceUrl && (
                <Image
                    className='object-cover rounded-xl shrink-0 w-1/3 aspect-video'
                    src={featuredImage.node.sourceUrl}
                    alt={featuredImage?.node?.altText || title}
                    title={featuredImage?.node?.title || title}
                    // 1. Fixed Aspect Ratio: 320x180 is a perfect 16:9 ratio to match 'aspect-video'
                    width={320}
                    height={180}
                    // 2. Fixed File Size: Tells Next.js to only serve a tiny ~150px image, saving 260 KiB!
                    sizes='(max-width: 768px) 33vw, 150px'
                />
            )}
            <div className={'flex flex-col p-2 gap-y-1 w-3/4 justify-center'}>
                <div className={'flex w-full flex-col gap-y-2'}>
                    <Link
                        href={uri}
                        className={`text-xs leading-[1.7] text-gray-800 dark:text-white/80 line-clamp-1`}
                    >
                        {title}
                    </Link>
                </div>
                <div className={'inline-flex items-center gap-2'}>
                    <NewsSentiment isSingle={false} newsSentiment={sentiment} newsId={databaseId} />
                    <div className='text-[10px] font-light text-black/70 dark:text-white/70 inline-flex gap-1 items-center'>
                        <TimeSinceClient date={date || ''} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewsCardSideBar;
